import React, { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useActivityTracker } from '../hooks/useActivityTracker';
import { SessionTimeoutWarning } from './SessionTimeoutWarning';

// Public routes where session timeout should not apply
const PUBLIC_ROUTE_PREFIXES = ['/pay/', '/login', '/signup', '/invite', '/reset-password'];

interface ActivityTrackerWrapperProps {
  children: React.ReactNode;
}

export const ActivityTrackerWrapper: React.FC<ActivityTrackerWrapperProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isPublicRoute = PUBLIC_ROUTE_PREFIXES.some(prefix => location.pathname.startsWith(prefix));

  const handleInactiveLogout = useCallback(async () => {
    await logout();
    // Store a flag to show the inactivity message on login page
    sessionStorage.setItem('logoutReason', 'inactivity');
    navigate('/login');
  }, [logout, navigate]);

  const {
    showWarning,
    secondsRemaining,
    resetTimer,
    triggerLogout
  } = useActivityTracker({
    isAuthenticated: !!user && !isPublicRoute,
    onInactiveLogout: handleInactiveLogout
  });

  const handleStayLoggedIn = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  const handleLogoutNow = useCallback(async () => {
    await logout();
    navigate('/login');
  }, [logout, navigate]);

  return (
    <>
      {children}
      <SessionTimeoutWarning
        isOpen={showWarning && !isPublicRoute}
        secondsRemaining={secondsRemaining}
        onStayLoggedIn={handleStayLoggedIn}
        onLogout={handleLogoutNow}
      />
    </>
  );
};
